import type { FlowchartTextLayout } from './flowchartTextLayout';

const DEFAULT_FONT_FAMILY = 'Inter, Arial, sans-serif';

const NUMBER_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 3,
  useGrouping: false,
});

const coerceString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const coerceNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const pickString = (...values: Array<unknown>): string | undefined => {
  for (const value of values) {
    const str = coerceString(value);
    if (str) return str;
  }
  return undefined;
};

const pickNumber = (...values: Array<unknown>): number | undefined => {
  for (const value of values) {
    const num = coerceNumber(value);
    if (num !== undefined) return num;
  }
  return undefined;
};

const coerceBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'true' || lower === '1') return true;
    if (lower === 'false' || lower === '0') return false;
  }
  if (typeof value === 'number') return value !== 0;
  return undefined;
};

const coerceDashArray = (value: unknown): number[] | undefined => {
  if (Array.isArray(value)) {
    const numbers = value.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
    return numbers.length > 0 ? numbers : undefined;
  }
  return undefined;
};

const pickBoolean = (...values: Array<unknown>): boolean | undefined => {
  for (const value of values) {
    const bool = coerceBoolean(value);
    if (bool !== undefined) return bool;
  }
  return undefined;
};

const pickDashArray = (...values: Array<unknown>): number[] | undefined => {
  for (const value of values) {
    const dash = coerceDashArray(value);
    if (dash) return dash;
  }
  return undefined;
};

export interface ShapePresentationDefaults {
  fill: string;
  stroke: string;
  strokeWidth: number;
  textColor: string;
  fontSize: number;
  fontFamily?: string;
}

export type HorizontalTextAlign = 'left' | 'center' | 'right';
export type VerticalTextAlign = 'top' | 'middle' | 'bottom';
export type LabelPlacement = 'inside' | 'below';

export interface ResolvedShapePresentation {
  fill: string;
  stroke: string;
  strokeWidth: number;
  textColor: string;
  fontSize: number;
  fontFamily: string;
  label: string | null;
  textAlign: HorizontalTextAlign;
  verticalAlign: VerticalTextAlign;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  labelPlacement: LabelPlacement;
  dash?: number[];
  dashEnabled?: boolean;
}

const resolveHorizontalTextAlign = (...values: Array<unknown>): HorizontalTextAlign => {
  for (const value of values) {
    const raw = coerceString(value)?.toLowerCase();
    if (!raw) continue;
    if (raw === 'left' || raw === 'start') return 'left';
    if (raw === 'right' || raw === 'end') return 'right';
    if (raw === 'center' || raw === 'middle') return 'center';
  }
  return 'center';
};

const resolveVerticalTextAlign = (...values: Array<unknown>): VerticalTextAlign => {
  for (const value of values) {
    const raw = coerceString(value)?.toLowerCase();
    if (!raw) continue;
    if (raw === 'top' || raw === 'hanging') return 'top';
    if (raw === 'bottom' || raw === 'baseline' || raw === 'alphabetic') return 'bottom';
    if (raw === 'middle' || raw === 'center') return 'middle';
  }
  return 'middle';
};

const resolveLabelPlacement = (...values: Array<unknown>): LabelPlacement => {
  for (const value of values) {
    const raw = coerceString(value)?.toLowerCase();
    if (raw === 'below') return 'below';
    if (raw === 'inside') return 'inside';
  }
  return 'inside';
};

const ensurePositive = (value: number | undefined, fallback: number): number => {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return value > 0 ? value : fallback;
};

export function resolveShapePresentation<TProps extends Record<string, unknown>>(
  props: TProps,
  viewModel: { style?: Record<string, unknown> } | null | undefined,
  defaults: ShapePresentationDefaults,
): ResolvedShapePresentation {
  const style = (viewModel?.style ?? {}) as Record<string, unknown>;
  const attrs = (props?.attrs as Record<string, unknown> | undefined) ?? {};

  const fill = pickString(
    props?.fillColor,
    attrs?.fillColor,
    style.fillColor,
    style.fill,
    defaults.fill,
  ) ?? defaults.fill;

  const stroke = pickString(
    props?.strokeColor,
    attrs?.strokeColor,
    style.strokeColor,
    style.stroke,
    defaults.stroke,
  ) ?? defaults.stroke;

  const strokeWidth = ensurePositive(
    pickNumber(props?.strokeWidth, attrs?.strokeWidth, style.strokeWidth),
    defaults.strokeWidth,
  );

  const textColor = pickString(
    props?.textColor,
    attrs?.textColor,
    style.textColor,
    defaults.textColor,
  ) ?? defaults.textColor;

  const label = pickString(
    props?.label,
    (props as Record<string, unknown>)?.text,
    attrs?.label,
    attrs?.text,
    style.text,
  ) ?? null;

  const fontSize = ensurePositive(
    pickNumber(
      (props as Record<string, unknown>)?.fontSize,
      attrs?.fontSize,
      style.fontSize,
    ),
    defaults.fontSize,
  );

  const fontFamily = pickString(
    (props as Record<string, unknown>)?.fontFamily,
    attrs?.fontFamily,
    style.fontFamily,
    defaults.fontFamily ?? DEFAULT_FONT_FAMILY,
  ) ?? DEFAULT_FONT_FAMILY;

  const dash = pickDashArray(
    (props as Record<string, unknown>)?.dash,
    attrs?.dash,
    style.dash,
  );

  const dashEnabled = pickBoolean(
    (props as Record<string, unknown>)?.dashEnabled,
    attrs?.dashEnabled,
    style.dashEnabled,
  ) ?? (dash !== undefined && dash.length > 0);

  const textAlign = resolveHorizontalTextAlign(
    (props as Record<string, unknown>)?.textAlign,
    attrs?.textAlign,
    style.textAlign,
    (props as Record<string, unknown>)?.align,
    attrs?.align,
    style.align,
  );

  const verticalAlign = resolveVerticalTextAlign(
    (props as Record<string, unknown>)?.verticalAlign,
    attrs?.verticalAlign,
    style.verticalAlign,
  );

  const fontWeight = pickString(
    (props as Record<string, unknown>)?.fontWeight,
    attrs?.fontWeight,
    style.fontWeight,
  );

  const fontStyle = pickString(
    (props as Record<string, unknown>)?.fontStyle,
    attrs?.fontStyle,
    style.fontStyle,
  );

  const textDecoration = pickString(
    (props as Record<string, unknown>)?.textDecoration,
    attrs?.textDecoration,
    style.textDecoration,
  );

  const labelPlacement = resolveLabelPlacement(
    (props as Record<string, unknown>)?.labelPlacement,
    attrs?.labelPlacement,
    style.labelPlacement,
  );

  return {
    fill,
    stroke,
    strokeWidth,
    textColor,
    fontSize,
    fontFamily,
    label,
    textAlign,
    verticalAlign,
    fontWeight,
    fontStyle,
    textDecoration,
    labelPlacement,
    dash,
    dashEnabled,
  };
}

export const escapeSvgAttr = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const escapeSvgText = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const formatSvgNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '0';
  return NUMBER_FORMATTER.format(value);
};

export interface CenteredLabelOptions {
  color: string;
  fontSize: number;
  fontFamily: string;
  padding?: number;
}

export interface BoxedLabelOptions extends CenteredLabelOptions {
  textAlign?: HorizontalTextAlign;
  verticalAlign?: VerticalTextAlign;
  fontWeight?: string | number;
  fontStyle?: string;
  textDecoration?: string;
  ellipsisOnOverflow?: boolean;
  lines?: string[];
  lineHeight?: number;
}

export interface ShapeLabelOptions {
  layout?: FlowchartTextLayout | null;
  lineHeight?: number;
}

export const LABEL_BELOW_GAP_PX = 5;
const LINE_HEIGHT_MULTIPLIER = 1.2;

export const computeBelowLabelBoxHeight = (fontSize: number): number => {
  const lineHeight = fontSize * LINE_HEIGHT_MULTIPLIER;
  return Math.max(lineHeight * 3 + 8, 48);
};

export const resolveFontStyleParts = (
  fontWeight?: unknown,
  fontStyle?: unknown,
): { fontWeight?: string | number; italic: boolean } => {
  let fw: string | number | undefined;
  const fwStr = coerceString(fontWeight)?.toLowerCase();
  if (fwStr) {
    if (fwStr === 'bold' || fwStr === '700') fw = '700';
    else if (/^[1-9]00$/.test(fwStr)) fw = fwStr;
    else fw = fontWeight as string;
  }

  const fsStr = coerceString(fontStyle)?.toLowerCase() ?? '';
  const italic = fsStr.includes('italic');
  if (!fw && (fsStr.includes('bold') || fsStr === '700')) {
    fw = '700';
  }

  return { fontWeight: fw, italic };
};

const estimateContentHeight = (lineCount: number, fontSize: number, lineHeightPx: number): number => {
  if (lineCount <= 0) return 0;
  return fontSize + Math.max(0, lineCount - 1) * lineHeightPx;
};

export const renderBoxedLabel = (
  label: string,
  boxX: number,
  boxY: number,
  boxWidth: number,
  boxHeight: number,
  options: BoxedLabelOptions,
): string => {
  if (!label.trim() || boxWidth <= 0 || boxHeight <= 0) return '';

  const padding = Math.max(0, options.padding ?? 12);
  const availableWidth = Math.max(0, boxWidth - padding * 2);
  const availableHeight = Math.max(0, boxHeight - padding * 2);
  const lineHeightPx = Math.max(1, options.lineHeight ?? options.fontSize * LINE_HEIGHT_MULTIPLIER);
  const lines = options.lines ?? wrapTextBlock(
    label,
    availableWidth,
    options.fontSize,
    Math.max(1, Math.floor(availableHeight / lineHeightPx) || 1),
    { ellipsisOnOverflow: options.ellipsisOnOverflow ?? false },
  );
  if (!lines.length) return '';

  const contentHeight = estimateContentHeight(lines.length, options.fontSize, lineHeightPx);
  const textAlign = options.textAlign ?? 'center';
  const verticalAlign = options.verticalAlign ?? 'middle';
  const fontParts = resolveFontStyleParts(options.fontWeight, options.fontStyle);

  let anchorX = boxX + padding;
  let textAnchor: 'start' | 'middle' | 'end' = 'start';
  if (textAlign === 'center') {
    anchorX = boxX + boxWidth / 2;
    textAnchor = 'middle';
  } else if (textAlign === 'right') {
    anchorX = boxX + boxWidth - padding;
    textAnchor = 'end';
  }

  let textY = boxY + padding;
  if (verticalAlign === 'middle') {
    textY = boxY + padding + Math.max(0, (availableHeight - contentHeight) / 2);
  } else if (verticalAlign === 'bottom') {
    textY = boxY + padding + Math.max(0, availableHeight - contentHeight);
  }

  return renderMultilineText(lines.join('\n'), {
    x: anchorX,
    y: textY,
    color: options.color,
    fontSize: options.fontSize,
    fontFamily: options.fontFamily,
    fontWeight: fontParts.fontWeight,
    fontStyle: fontParts.italic ? 'italic' : undefined,
    textDecoration: options.textDecoration,
    textAnchor,
    dominantBaseline: 'hanging',
    lineHeightPx,
  });
};

export const renderShapeLabel = (
  presentation: ResolvedShapePresentation,
  width: number,
  height: number,
  padding = 12,
  options: ShapeLabelOptions = {},
): string => {
  if (!presentation.label) return '';

  const fontParts = resolveFontStyleParts(
    options.layout?.fontWeight ?? presentation.fontWeight,
    options.layout?.fontStyle ?? presentation.fontStyle,
  );
  const labelOptions: BoxedLabelOptions = {
    color: presentation.textColor,
    fontSize: presentation.fontSize,
    fontFamily: options.layout?.fontFamily ?? presentation.fontFamily,
    padding,
    textAlign: presentation.textAlign,
    verticalAlign: presentation.verticalAlign,
    fontWeight: fontParts.fontWeight,
    fontStyle: fontParts.italic ? 'italic' : undefined,
    textDecoration: presentation.textDecoration,
    lines: options.layout?.lines,
    lineHeight: options.layout
      ? presentation.fontSize * options.layout.lineHeight
      : options.lineHeight ? presentation.fontSize * options.lineHeight : undefined,
  };

  if (presentation.labelPlacement === 'below') {
    const labelY = height + LABEL_BELOW_GAP_PX;
    const labelH = computeBelowLabelBoxHeight(presentation.fontSize);
    return renderBoxedLabel(presentation.label, 0, labelY, width, labelH, {
      ...labelOptions,
      verticalAlign: 'top',
      padding: 0,
    });
  }

  return renderBoxedLabel(presentation.label, 0, 0, width, height, labelOptions);
};

const estimateTextWidthPx = (text: string, fontSize: number): number => {
  const wide = new Set(['w', 'm', 'W', 'M']);
  const narrow = new Set(['i', 'l', 'I', 'j', 't', 'f', 'r', '.', ',', ':', ';', '\'', '"', '|', '!', '`']);
  const space = new Set([' ', '\t']);

  let em = 0;
  for (const ch of Array.from(text)) {
    if (wide.has(ch)) em += 0.95;
    else if (narrow.has(ch)) em += 0.35;
    else if (space.has(ch)) em += 0.33;
    else em += 0.62;
  }

  const px = em * fontSize;
  const buffer = Math.max(4, fontSize * 0.2);
  return Math.max(0, px + buffer);
};

const truncateWithEllipsis = (value: string): string => {
  const trimmed = value.trimEnd();
  if (trimmed.length <= 1) return '…';
  return `${trimmed.slice(0, Math.max(1, trimmed.length - 1))}…`;
};

const wrapLineToWidth = (line: string, maxWidthPx: number, fontSize: number): string[] => {
  const cleaned = line.replace(/\s+/g, ' ').trim();
  if (!cleaned) return [''];
  if (maxWidthPx <= 0) return [cleaned];
  if (estimateTextWidthPx(cleaned, fontSize) <= maxWidthPx) return [cleaned];

  const words = cleaned.split(' ');
  const out: string[] = [];
  let current = '';

  const pushCurrent = () => {
    if (current.trim().length) out.push(current.trim());
    current = '';
  };

  for (const word of words) {
    if (!word) continue;
    const next = current ? `${current} ${word}` : word;
    if (estimateTextWidthPx(next, fontSize) <= maxWidthPx) {
      current = next;
      continue;
    }

    if (current) {
      pushCurrent();
    }

    if (estimateTextWidthPx(word, fontSize) <= maxWidthPx) {
      current = word;
      continue;
    }

    // Hard-break a very long word to fit width.
    let remaining = word;
    while (remaining.length) {
      let cut = Math.min(remaining.length, Math.max(1, Math.floor(maxWidthPx / Math.max(1, fontSize * 0.6))));
      cut = Math.max(1, cut);
      while (cut < remaining.length && estimateTextWidthPx(remaining.slice(0, cut), fontSize) > maxWidthPx) {
        cut = Math.max(1, cut - 1);
      }
      while (cut < remaining.length && estimateTextWidthPx(remaining.slice(0, cut + 1), fontSize) <= maxWidthPx) {
        cut += 1;
      }
      out.push(remaining.slice(0, cut));
      remaining = remaining.slice(cut);
    }
  }

  pushCurrent();
  return out.length ? out : [''];
};

const wrapTextBlock = (
  text: string,
  maxWidthPx: number,
  fontSize: number,
  maxLines: number,
  options: { ellipsisOnOverflow?: boolean } = {},
): string[] => {
  const normalized = normaliseSvgLineEndings(text);
  const explicitLines = normalized.split('\n');
  const wrapped: string[] = [];
  let hasOverflow = false;

  for (const line of explicitLines) {
    const parts = wrapLineToWidth(line, maxWidthPx, fontSize);
    for (const part of parts) {
      if (wrapped.length >= maxLines) {
        hasOverflow = true;
        break;
      }
      wrapped.push(part);
    }
    if (wrapped.length >= maxLines) {
      hasOverflow = true;
      break;
    }
  }

  if (wrapped.length === 0) return [];
  const clipped = wrapped.length > maxLines ? wrapped.slice(0, maxLines) : wrapped;
  const ellipsisOnOverflow = options.ellipsisOnOverflow !== false;
  if (ellipsisOnOverflow && hasOverflow && clipped.length > 0) {
    clipped[clipped.length - 1] = truncateWithEllipsis(clipped[clipped.length - 1] ?? '');
  } else if (ellipsisOnOverflow && clipped.length === maxLines) {
    const last = clipped[clipped.length - 1] ?? '';
    const originalNoNewlines = normalized.replace(/\n/g, ' ').trim();
    const reconstructed = clipped.join(' ').trim();
    if (originalNoNewlines.length > reconstructed.length + 1) {
      clipped[clipped.length - 1] = truncateWithEllipsis(last);
    }
  }

  return clipped;
};

export interface MultilineTextOptions {
  x: number;
  y: number;
  color: string;
  fontSize: number;
  fontFamily: string;
  fontWeight?: string | number;
  fontStyle?: string;
  textDecoration?: string;
  textAnchor: 'start' | 'middle' | 'end';
  opacity?: number;
  lineHeightPx?: number;
  dominantBaseline?: string;
}

const normaliseSvgLineEndings = (value: string): string => value.replace(/\r\n?/g, '\n');

export const renderMultilineText = (text: string, options: MultilineTextOptions): string => {
  if (!text.trim()) return '';
  const normalized = normaliseSvgLineEndings(text);
  const lines = normalized.split('\n');
  const x = formatSvgNumber(options.x);
  const y = formatSvgNumber(options.y);
  const lineHeightPx = Math.max(
    1,
    Number.isFinite(options.lineHeightPx) ? Number(options.lineHeightPx) : options.fontSize * 1.2,
  );

  const attrs: string[] = [
    `x="${x}"`,
    `y="${y}"`,
    `text-anchor="${options.textAnchor}"`,
    `fill="${escapeSvgAttr(options.color)}"`,
    `font-size="${formatSvgNumber(options.fontSize)}"`,
    `font-family="${escapeSvgAttr(options.fontFamily)}"`,
    `xml:space="preserve"`,
  ];
  if (options.fontWeight !== undefined && options.fontWeight !== null) {
    attrs.push(`font-weight="${escapeSvgAttr(String(options.fontWeight))}"`);
  }
  if (options.fontStyle) {
    attrs.push(`font-style="${escapeSvgAttr(options.fontStyle)}"`);
  }
  if (options.textDecoration && options.textDecoration !== 'none') {
    attrs.push(`text-decoration="${escapeSvgAttr(options.textDecoration)}"`);
  }
  if (typeof options.opacity === 'number' && Number.isFinite(options.opacity)) {
    attrs.push(`opacity="${formatSvgNumber(options.opacity)}"`);
  }
  if (options.dominantBaseline) {
    attrs.push(`dominant-baseline="${escapeSvgAttr(options.dominantBaseline)}"`);
  }

  const tspans = lines
    .map((line, index) => {
      const dy = index === 0 ? '' : ` dy="${formatSvgNumber(lineHeightPx)}"`;
      const content = line.length ? escapeSvgText(line) : '&#8203;';
      return `<tspan x="${x}"${dy}>${content}</tspan>`;
    })
    .join('');

  return `<text ${attrs.join(' ')}>${tspans}</text>`;
};

export const renderCenteredLabel = (
  label: string,
  width: number,
  height: number,
  options: CenteredLabelOptions,
): string => {
  if (!label.trim()) return '';
  const padding = Math.max(0, options.padding ?? 12);
  const x = width / 2;
  const y = height / 2;
  const availableWidth = Math.max(0, width - padding * 2);
  const availableHeight = Math.max(0, height - padding * 2);
  const lineHeightPx = Math.max(1, options.fontSize * 1.2);
  const maxLines = Math.max(1, Math.floor(availableHeight / lineHeightPx) || 1);

  const lines = wrapTextBlock(label, availableWidth, options.fontSize, maxLines);
  if (!lines.length) return '';

  const centerX = formatSvgNumber(x);
  const centerY = formatSvgNumber(y);
  const blockOffset = lines.length > 1 ? -((lines.length - 1) / 2) * lineHeightPx : 0;

  const tspans = lines
    .map((line, index) => {
      const dyValue = index === 0 ? blockOffset : lineHeightPx;
      const dyAttr = dyValue ? ` dy="${formatSvgNumber(dyValue)}"` : '';
      const content = line.length ? escapeSvgText(line) : '&#8203;';
      return `<tspan x="${centerX}"${dyAttr}>${content}</tspan>`;
    })
    .join('');

  return (
    `<text x="${centerX}" y="${centerY}" text-anchor="middle" ` +
    `dominant-baseline="middle" fill="${escapeSvgAttr(options.color)}" ` +
    `font-size="${formatSvgNumber(options.fontSize)}" ` +
    `font-family="${escapeSvgAttr(options.fontFamily)}" ` +
    `xml:space="preserve" data-max-width="${formatSvgNumber(availableWidth)}">${tspans}</text>`
  );
};
